import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { sendWhatsApp } from "@/lib/whatsapp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { UsersRound, Plus, Loader2, Copy, KeyRound, ExternalLink, Trash2, ChevronDown, ChevronUp, Shield, Power, Image as ImageIcon, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import FileUploader, { fullUrl } from "@/components/FileUploader";

const PERMISSION_LABELS = {
  read: "Visualizar", write: "Criar/Editar", delete: "Excluir",
};

// MODULE_LABELS deve ficar sincronizado com Configurações → Recursos
// (ALL_MODULES em TenantSettings.jsx). No pivot Pet/Barbearia/Beleza (iter_21+24)
// foram removidos: crm, funil_comercial, servicos, vendas, pacotes, assinaturas,
// whatsapp, automacoes, relatorios.
const MODULE_LABELS = {
  dashboard: "Visão Geral",
  clientes: "Clientes",
  agenda: "Agenda",
  financeiro: "Financeiro",
  campanhas: "Mensagens & Campanhas",
  pesquisa_satisfacao: "Pesquisa de Satisfação",
  fidelidade: "Programa de Fidelidade",
  clube_beneficios: "Clube de Benefícios",
  indicacao_premiada: "Indicações",
  configuracoes: "Configurações",
};

function PermissionsMatrix({ available, value, onChange, disabled }) {
  const has = (k) => value.includes(k);
  const toggle = (k) => {
    if (disabled) return;
    onChange(has(k) ? value.filter((p) => p !== k) : [...value, k]);
  };
  return (
    <div className="rounded-lg border overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr>
            <th className="text-left p-2 text-xs font-medium">Módulo</th>
            {Object.entries(PERMISSION_LABELS).map(([a, l]) => (
              <th key={a} className="text-center p-2 text-xs font-medium">{l}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.entries(available).map(([mod, actions]) => (
            <tr key={mod} className="border-t">
              <td className="p-2 text-sm">{MODULE_LABELS[mod] || mod}</td>
              {actions.map((a) => {
                const key = `${mod}:${a}`;
                return (
                  <td key={a} className="text-center p-2">
                    <input
                      type="checkbox"
                      data-testid={`perm-${key}`}
                      checked={has(key)}
                      onChange={() => toggle(key)}
                      disabled={disabled}
                      className="w-4 h-4 cursor-pointer accent-primary"
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CreateMemberDialog({ open, onOpenChange, presets, available, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [preset, setPreset] = useState("atendente");
  const [permissions, setPermissions] = useState([]);
  const [advanced, setAdvanced] = useState(false);
  const [pwdMode, setPwdMode] = useState("auto"); // "auto" | "custom"
  const [customPwd, setCustomPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  // Sync `preset` to the first available key as soon as presets load.
  // Fixes the bug where the Select trigger appeared empty because the
  // hard-coded default "atendente" didn't yet exist in `presets` when the
  // dialog mounted.
  const presetKeys = Object.keys(presets);
  useEffect(() => {
    if (!presetKeys.length) return;
    if (!presets[preset]) {
      const next = presetKeys.includes("atendente") ? "atendente" : presetKeys[0];
      setPreset(next);
      setPermissions(presets[next]?.permissions || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetKeys.join("|")]);

  useEffect(() => {
    if (presets[preset]) setPermissions(presets[preset].permissions || []);
  }, [preset, presets]);

  const reset = () => {
    setName(""); setEmail(""); setPhone(""); setPreset("atendente");
    setPermissions(presets["atendente"]?.permissions || []);
    setAdvanced(false); setPwdMode("auto"); setCustomPwd(""); setCreated(null);
  };

  const submit = async () => {
    if (!name.trim() || !email.trim()) return toast.error("Nome e email são obrigatórios.");
    if (pwdMode === "custom" && customPwd.length < 6) return toast.error("Senha deve ter ao menos 6 caracteres.");
    setBusy(true);
    try {
      const { data } = await api.post("/tenant/team", {
        name, email: email.toLowerCase(), phone,
        role_preset: preset,
        permissions_override: advanced ? permissions : null,
        custom_password: pwdMode === "custom" ? customPwd : null,
      });
      setCreated(data);
      toast.success("Funcionário criado!");
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copiado!"); };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Novo funcionário</DialogTitle>
          <DialogDescription>
            Cadastre um membro da equipe. Senha temporária é gerada e enviada por WhatsApp.
          </DialogDescription>
        </DialogHeader>

        {!created ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Nome completo *</Label>
                <Input data-testid="team-name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div><Label>Telefone / WhatsApp</Label>
                <Input data-testid="team-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="5511999990000" />
              </div>
            </div>
            <div>
              <Label>E-mail (login) *</Label>
              <Input data-testid="team-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label>Perfil de acesso</Label>
              <Select value={preset} onValueChange={setPreset}>
                <SelectTrigger data-testid="team-preset"><SelectValue placeholder={presetKeys.length ? "Selecione um perfil" : "Carregando perfis..."} /></SelectTrigger>
                <SelectContent>
                  {Object.entries(presets).map(([k, p]) => (
                    <SelectItem key={k} value={k}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">{presets[preset]?.description}</p>
            </div>

            {/* Senha — auto ou definida pelo admin */}
            <div className="space-y-2 border-t pt-3">
              <Label>Senha de acesso</Label>
              <div className="flex flex-wrap gap-2">
                <Badge
                  variant={pwdMode === "auto" ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => setPwdMode("auto")}
                  data-testid="team-pwd-auto"
                >🎲 Gerar automaticamente</Badge>
                <Badge
                  variant={pwdMode === "custom" ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => setPwdMode("custom")}
                  data-testid="team-pwd-custom"
                >🔐 Definir senha agora</Badge>
              </div>
              {pwdMode === "custom" && (
                <Input
                  data-testid="team-pwd-input"
                  type="text"
                  value={customPwd}
                  onChange={(e) => setCustomPwd(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="font-mono"
                />
              )}
              <p className="text-xs text-muted-foreground">
                {pwdMode === "auto"
                  ? "O sistema gera uma senha temporária. O funcionário troca no 1º acesso."
                  : "Você define a senha agora. O funcionário NÃO será obrigado a trocar."}
              </p>
            </div>

            <button
              type="button"
              onClick={() => setAdvanced(!advanced)}
              className="text-sm underline text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              {advanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {advanced ? "Esconder" : "Personalizar"} permissões
            </button>

            {advanced && (
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground">
                  Personalize quais ações este funcionário pode realizar. Mudanças aqui sobrescrevem o perfil.
                </div>
                <PermissionsMatrix
                  available={available}
                  value={permissions}
                  onChange={setPermissions}
                />
                <div className="text-xs text-muted-foreground">
                  {permissions.length} permissão(ões) selecionada(s).
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-4 space-y-3">
              <div className="flex items-center gap-2 font-semibold text-amber-900 dark:text-amber-200">
                <KeyRound className="w-4 h-4" /> Anote esta senha — não será exibida novamente
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 rounded bg-background font-mono text-lg tracking-widest">{created.temp_password}</code>
                <Button size="sm" variant="outline" onClick={() => copy(created.temp_password)}><Copy className="w-3 h-3" /></Button>
              </div>
              <p className="text-xs text-muted-foreground">Funcionário deverá trocar no primeiro acesso.</p>
            </div>
            {created.whatsapp_link && (
              <a href={created.whatsapp_link} target="_blank" rel="noopener noreferrer">
                <Button className="w-full" data-testid="team-send-whatsapp">
                  <ExternalLink className="w-4 h-4 mr-2" /> Enviar boas-vindas pelo WhatsApp
                </Button>
              </a>
            )}
            <Button variant="outline" className="w-full" onClick={() => copy(created.welcome_message)}>
              <Copy className="w-4 h-4 mr-2" /> Copiar mensagem completa
            </Button>
          </div>
        )}

        <DialogFooter>
          {!created ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button data-testid="team-submit" onClick={submit} disabled={busy}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Criar e gerar senha
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)} className="w-full sm:w-auto">Concluir</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditProfileDialog({ open, onOpenChange, member, onSaved }) {
  const [avatar, setAvatar] = useState("");
  const [bio, setBio] = useState("");
  const [specialties, setSpecialties] = useState("");
  const [portfolio, setPortfolio] = useState("");
  const [instagram, setInstagram] = useState("");
  const [accepting, setAccepting] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!member) return;
    setAvatar(member.avatar_url || "");
    setBio(member.bio || "");
    setSpecialties((member.specialties || []).join(", "));
    setPortfolio((member.portfolio_images || []).join("\n"));
    setInstagram(member.instagram || "");
    setAccepting(member.accepting_appointments !== false);
  }, [member]);

  if (!member) return null;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/tenant/team/${member.id}`, {
        avatar_url: avatar.trim(),
        bio: bio.trim(),
        specialties: specialties.split(",").map((s) => s.trim()).filter(Boolean),
        portfolio_images: portfolio.split("\n").map((s) => s.trim()).filter(Boolean),
        instagram: instagram.trim(),
        accepting_appointments: accepting,
      });
      toast.success("Perfil atualizado!");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Perfil de {member.name}</DialogTitle>
          <DialogDescription>
            Foto, bio e portfólio aparecem para o cliente escolher na hora de agendar.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Foto de perfil (JPEG)</Label>
            <FileUploader
              testid="profile-avatar"
              value={avatar}
              onChange={setAvatar}
              accept="image/jpeg"
            />
            {avatar && (
              <img src={fullUrl(avatar)} alt="" className="mt-2 w-20 h-20 rounded-full object-cover border-2 border-primary" onError={(e) => e.target.style.display="none"} />
            )}
          </div>
          <div>
            <Label>Bio / Apresentação (até 600 caracteres)</Label>
            <Textarea data-testid="profile-bio" value={bio} onChange={(e) => setBio(e.target.value.slice(0, 600))} rows={3} placeholder="Apresente-se: tempo de experiência, formação, estilo..." />
            <div className="text-[10px] text-muted-foreground text-right">{bio.length}/600</div>
          </div>
          <div>
            <Label>Especialidades (separar por vírgula)</Label>
            <Input data-testid="profile-specialties" value={specialties} onChange={(e) => setSpecialties(e.target.value)} placeholder="Corte degradê, Barba modelada, Coloração..." />
          </div>
          <div>
            <Label>Portfólio (JPEG ou PDF — até 100 arquivos, os mais antigos serão substituídos automaticamente)</Label>
            <FileUploader
              testid="profile-portfolio"
              value={(portfolio || "").split("\n").filter(Boolean)}
              onChange={(arr) => {
                // FIFO: quando ultrapassa 100, remove os mais antigos.
                const trimmed = arr.length > 100 ? arr.slice(arr.length - 100) : arr;
                setPortfolio(trimmed.join("\n"));
              }}
              multi
              accept="image/jpeg,application/pdf"
              max={100}
            />
            <div className="text-[10px] text-muted-foreground mt-1 text-right">
              {(portfolio || "").split("\n").filter(Boolean).length}/100 arquivos
            </div>
          </div>
          <div>
            <Label>Instagram (sem @)</Label>
            <Input value={instagram} onChange={(e) => setInstagram(e.target.value.replace("@", ""))} placeholder="seuperfil" />
          </div>
          <div className="flex items-center gap-2 p-3 rounded-lg border bg-emerald-50 dark:bg-emerald-900/20">
            <input type="checkbox" checked={accepting} onChange={(e) => setAccepting(e.target.checked)} className="w-4 h-4 accent-primary" />
            <div className="flex-1 text-sm">
              <div className="font-medium">Aceitando agendamentos</div>
              <div className="text-xs text-muted-foreground">Desmarque se este profissional não está atendendo no momento.</div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="profile-save" onClick={save} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ImageIcon className="w-4 h-4 mr-2" />}
            Salvar perfil
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TenantTeam() {
  const [items, setItems] = useState([]);
  const [presets, setPresets] = useState({});
  const [available, setAvailable] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [profileMember, setProfileMember] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/tenant/team"),
        api.get("/tenant/team/role-presets"),
      ]);
      setItems(a.data.items || []);
      setPresets(b.data.presets || {});
      setAvailable(b.data.available_permissions || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const [pwdModal, setPwdModal] = useState(null); // { email, password, name, phone }

  const resetPwd = async (u) => {
    try {
      const { data } = await api.post(`/tenant/team/${u.id}/reset-password`);
      setPwdModal({ email: u.email, password: data.temp_password, name: u.name, phone: u.phone });
      try { navigator.clipboard.writeText(data.temp_password); } catch { /* ignore */ }
      toast.success("Nova senha gerada e copiada.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remover ${u.name}?`)) return;
    try {
      await api.delete(`/tenant/team/${u.id}`);
      toast.success("Funcionário removido.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleStatus = async (u) => {
    const next = u.status === "active" ? "suspended" : "active";
    try {
      await api.patch(`/tenant/team/${u.id}`, { status: next });
      toast.success(next === "active" ? "Reativado." : "Suspenso.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Equipe · acesso controlado</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <UsersRound className="w-8 h-8" /> Funcionários
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Apenas o(a) Administrador(a) pode criar funcionários e definir o que cada um vê e faz.
          </p>
        </div>
        <Button data-testid="team-new" size="lg" onClick={() => setOpen(true)}>
          <Plus className="w-4 h-4 mr-2" /> Novo funcionário
        </Button>
      </div>

      {loading ? (
        <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-sm text-muted-foreground rounded-tenant">
          Nenhum funcionário cadastrado ainda.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((u) => {
            const isAdmin = u.role === "tenant_admin";
            return (
              <Card key={u.id} data-testid={`team-${u.id}`} className="p-4 rounded-tenant">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex gap-3 min-w-0 flex-1">
                    {u.avatar_url ? (
                      <img src={fullUrl(u.avatar_url)} alt={u.name} className="w-12 h-12 rounded-full object-cover border-2 border-primary shrink-0" onError={(e) => e.target.style.display = "none"} />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center font-bold text-sm shrink-0">{u.name?.[0] || "?"}</div>
                    )}
                    <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold leading-tight truncate flex items-center gap-2">
                      {u.name}
                      {isAdmin && <Badge className="text-[10px]"><Shield className="w-2.5 h-2.5 mr-0.5 inline" />Admin</Badge>}
                      {u.must_change_password && !isAdmin && <Badge variant="outline" className="text-[10px]">Senha temporária</Badge>}
                      {u.status === "suspended" && <Badge variant="destructive" className="text-[10px]">Suspenso</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 truncate">{u.email}</div>
                    {u.phone && <div className="text-xs text-muted-foreground truncate">{u.phone}</div>}
                    {!isAdmin && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        <Badge variant="secondary" className="text-[10px]">
                          {presets[u.role_preset]?.label || u.role_preset || "Customizado"}
                        </Badge>
                        <Badge variant="outline" className="text-[10px]">{(u.permissions || []).length} permissões</Badge>
                        {u.accepting_appointments === false && <Badge variant="destructive" className="text-[10px]">Não atende</Badge>}
                      </div>
                    )}
                    </div>
                  </div>
                </div>
                {!isAdmin && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => setProfileMember(u)} data-testid={`team-edit-profile-${u.id}`}>
                      <ImageIcon className="w-3 h-3 mr-1" /> Perfil
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => resetPwd(u)}>
                      <KeyRound className="w-3 h-3 mr-1" /> Resetar senha
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => toggleStatus(u)}>
                      <Power className="w-3 h-3 mr-1" /> {u.status === "active" ? "Suspender" : "Reativar"}
                    </Button>
                    <Button size="sm" variant="ghost" className="text-xs text-destructive ml-auto" onClick={() => remove(u)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <CreateMemberDialog
        open={open}
        onOpenChange={setOpen}
        presets={presets}
        available={available}
        onCreated={load}
      />
      <EditProfileDialog
        open={!!profileMember}
        onOpenChange={(o) => !o && setProfileMember(null)}
        member={profileMember}
        onSaved={load}
      />

      {/* Modal com senha temporária + envio via WhatsApp */}
      <Dialog open={!!pwdModal} onOpenChange={(o) => !o && setPwdModal(null)}>
        <DialogContent className="max-w-md" data-testid="team-pwd-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><KeyRound className="w-5 h-5" /> Nova senha gerada</DialogTitle>
            <DialogDescription>
              Copie a nova senha ou envie direto para o funcionário via WhatsApp. Ele será obrigado a trocá-la no 1º login.
            </DialogDescription>
          </DialogHeader>
          {pwdModal && (
            <div className="space-y-3">
              <div className="rounded-md border p-3 bg-muted/50 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] uppercase w-16 text-muted-foreground">E-mail</span>
                  <code className="flex-1 text-xs font-mono truncate">{pwdModal.email}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] uppercase w-16 text-muted-foreground">Senha</span>
                  <code className="flex-1 text-sm font-mono font-bold" data-testid="team-pwd-value">{pwdModal.password}</code>
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(pwdModal.password); toast.success("Senha copiada!"); }}>
                    Copiar
                  </Button>
                </div>
              </div>
              {pwdModal.phone && (
                <Button className="w-full gap-2" onClick={() => {
                  const msg =
                    `Olá ${pwdModal.name}! 👋\n\nSua nova senha de acesso:\n📧 ${pwdModal.email}\n🔑 ${pwdModal.password}\n\nTroque a senha no primeiro login.`;
                  sendWhatsApp({ phone: pwdModal.phone, text: msg, context: "team_credentials" });
                }}>
                  <MessageCircle className="w-4 h-4" /> Enviar via WhatsApp
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
